/**
 * renderer/pixi/cell-style.ts
 *
 * How filled cells (and clues) look. Cells always stay full, square, and edge
 * to edge (no gaps, no rounded corners) so the picture reads as one image.
 *
 *   blocks  — every filled cell gets a light top/left and dark bottom/right
 *             bevel, like a wall of square blocks. The default.
 *   regions — each same-colored region is lit like one raised slab: the bevel
 *             only on edges where the neighbour is a different color (or the
 *             board edge).
 *   flat    — plain squares and plain clue boxes (the original look).
 *
 * With either bevel, clues are drawn as rounded, bevelled tiles with small gaps.
 */

import type { CellStyle } from '../board-renderer';

export const CELL_STYLES: readonly CellStyle[] = ['blocks', 'regions', 'flat'];
export const DEFAULT_CELL_STYLE: CellStyle = 'blocks';

export function isCellStyle(value: unknown): value is CellStyle {
  return typeof value === 'string' && (CELL_STYLES as readonly string[]).includes(value);
}

/** Whether filled cells get a bevel (and clues the tile look). */
export function hasBevel(style: CellStyle): boolean {
  return style !== 'flat';
}

/** Edge bits of a cell's bevel mask: set where the edge is exposed. */
export const EDGE = { TOP: 1, RIGHT: 2, BOTTOM: 4, LEFT: 8 } as const;
/** A lone cell: every edge exposed. */
export const ALL_EDGES = 15;

/**
 * Which of a filled cell's edges are exposed: the neighbour across it is off
 * the board or holds a different value. 0 for a cell that isn't filled.
 */
export function exposedEdges(board: readonly number[], index: number, rows: number, columns: number): number {
  const value = board[index];
  if (!(value >= 1)) return 0;
  const row = Math.floor(index / columns);
  const col = index % columns;
  let mask = 0;
  if (row === 0 || board[index - columns] !== value) mask |= EDGE.TOP;
  if (col === columns - 1 || board[index + 1] !== value) mask |= EDGE.RIGHT;
  if (row === rows - 1 || board[index + columns] !== value) mask |= EDGE.BOTTOM;
  if (col === 0 || board[index - 1] !== value) mask |= EDGE.LEFT;
  return mask;
}

/**
 * A cell's bevel mask in a style: every edge for `blocks`, the exposed edges
 * for `regions`, none for `flat` (and none for cells that aren't filled).
 */
export function bevelEdges(style: CellStyle, board: readonly number[], index: number, rows: number, columns: number): number {
  switch (style) {
    case 'blocks':
      return board[index] >= 1 ? ALL_EDGES : 0;
    case 'regions':
      return exposedEdges(board, index, rows, columns);
    case 'flat':
      return 0;
  }
}

/** A cell and its four neighbours (on the board), for re-evaluating bevels after a change. */
export function withNeighbours(indices: readonly number[], rows: number, columns: number): number[] {
  const out = new Set<number>();
  const total = rows * columns;
  for (const index of indices) {
    if (index < 0 || index >= total) continue;
    out.add(index);
    const col = index % columns;
    if (index >= columns) out.add(index - columns);
    if (index + columns < total) out.add(index + columns);
    if (col > 0) out.add(index - 1);
    if (col < columns - 1) out.add(index + 1);
  }
  return [...out];
}
